// src/modules/app-views/omnichannel/pages/Init/components/ChatMenu.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect } from 'react';
import { theme } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import omnichannelService from '../../../service';
import useZustand from 'hooks/useZustand';
import { connect as sseConnect, subscribe as sseSubscribe } from './omnichannelSseManager';
import ChatMenuHeader from './ChatMenuHeader';
import ChatMenuBody from './ChatMenuBody';

// Module-level conversation cache  - survives remounts caused by route changes
// between /omnichannel (init page) and /omnichannel/:_id (conversation page).
// Both pages wrap OmnichannelInit in separate withModuleSetup HOCs, causing
// full remount on navigation. The cache ensures the list appears instantly
// without a loading spinner on subsequent mounts.
let _conversationsCache = null;

// Tracks which subscriptionId the cache was built for.
// If the subscription changes (tenant switch), the cache is invalidated.
let _cacheSubscriptionId = null;

// Flag to ensure auto-switch to "closed" viewMode runs only once on initial page load.
// Module-level so it survives component remounts (route changes within omnichannel).
let _autoSwitchDone = false;

/**
 * Update contactSnapshot for a specific conversation in the module-level cache.
 * Called by Conversation.jsx after getById returns fresh data — ensures ChatMenu
 * shows updated name/photo/description immediately on next render (remount).
 */
/**
 * Update audio duration for a message in the cache (called by MessageBubble after browser loads metadata).
 */
export function updateMessageDurationInCache(conversationId, externalMessageId, duration) {
  if (!conversationId || !externalMessageId || !duration) return;
  const updater = prev => prev.map(c => {
    if (c._id !== conversationId) return c;
    const msgs = c.messages?.map(m =>
      m.externalMessageId === externalMessageId ? { ...m, content: { ...m.content, duration } } : m
    );
    return { ...c, messages: msgs };
  });
  // Update module-level cache
  if (_conversationsCache) _conversationsCache = updater(_conversationsCache);
  // Update React state (triggers re-render)
  _onSseEvent?.(updater);
}

export function updateConversationSnapshot(conversationId, contactSnapshot) {
  if (!_conversationsCache || !conversationId || !contactSnapshot) return;
  _conversationsCache = _conversationsCache.map(c =>
    c._id === conversationId
      ? { ...c, contactSnapshot: { ...c.contactSnapshot, ...contactSnapshot } }
      : c
  );
}

// Module-level callback ref  - SSE event handlers call this instead of a closed-over
// updateConversations. Updated on every mount so it always points to the live component.
let _onSseEvent = null;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ChatMenu = ({ basePath, onConversationsLoaded }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();

  // Zustand  - subscription identifies the SSE channel for this tenant
  const storedSubscription = useZustand('auth', 'subscription');
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');
  const slaConfig = storedParameters?.modules?.omnichannel?.sla || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const location = useLocation();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Only use the module-level cache if it was built for the current subscription.
  // If the subscription changed (tenant switch), discard the stale cache immediately.
  const currentSubscriptionId = storedSubscription?._id;
  const cacheIsValid = _conversationsCache !== null && _cacheSubscriptionId === currentSubscriptionId;
  // Initialize from cache so remounts show data immediately (no spinner flash)
  const [conversations, setConversations] = useState(cacheIsValid ? _conversationsCache : []);
  const [loading, setLoading] = useState(!cacheIsValid);
  const [searchValue, setSearchValue] = useState('');
  const [hoveredId, setHoveredId] = useState(null);
  const [openKeys, setOpenKeys] = useState(null); // null = not yet initialized
  const [showGroups, setShowGroups] = useState(() => loadFilterPref(storedUser?._id, 'showGroups'));
  const [showArchived, setShowArchived] = useState(() => loadFilterPref(storedUser?._id, 'showArchived'));
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'closed'
  const [closedConversations, setClosedConversations] = useState([]);
  const [loadingClosed, setLoadingClosed] = useState(false);
  // SLA tick counter  - increments every second to force re-render of SLA countdowns
  const [, setSlaTick] = useState(0);
  // Refresh trigger  - incremented by external events (e.g., orphan cleanup) to force re-fetch
  const [refreshKey, setRefreshKey] = useState(0);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Listen for conversation refresh events (e.g., after orphan cleanup)
  useEffect(() => {
    const handleRefresh = () => {
      _conversationsCache = null;
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('omnichannel:conversations-refresh', handleRefresh);
    return () => window.removeEventListener('omnichannel:conversations-refresh', handleRefresh);
  }, []);

  useEffect(() => {
    if (!currentSubscriptionId) return;

    // Invalidate the module-level cache when subscription changes (tenant switch).
    // Prevents conversations from a previous subscription leaking into the new one.
    if (_cacheSubscriptionId && _cacheSubscriptionId !== currentSubscriptionId) {
      _conversationsCache = null;
      _autoSwitchDone = false;
      setConversations([]);
      setClosedConversations([]);
    }
    _cacheSubscriptionId = currentSubscriptionId;

    let active = true;

    const fetchConversations = async () => {
      // Only show spinner on truly first load (no cached data yet)
      if (_conversationsCache === null) setLoading(true);
      try {
        const [data] = await omnichannelService.getConversations({
          size: 100,
          filter: { status: ['open', 'attending', 'waiting'] },
        });
        if (active) {
          const list = Array.isArray(data) ? data : [];
          // Sort most recent first on initial load
          list.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
          updateConversations(list);

          // If the currently selected conversation was removed (e.g., orphan cleanup),
          // navigate back to the base path so the empty state is shown
          const currentId = location.pathname.split('/').pop();
          if (currentId && /^[a-f0-9]{24}$/.test(currentId) && !list.some(c => c._id === currentId)) {
            navigate(basePath, { replace: true });
          }
        }
      } catch {
        // Graceful degradation: show empty list if backend is offline
        if (active && _conversationsCache === null) {
          updateConversations([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    // Fetch closed conversations count for badge (non-blocking)
    const fetchClosedCount = async () => {
      try {
        const [data] = await omnichannelService.getConversations({
          size: 100,
          filter: { status: ['resolved'] },
        });
        if (active) {
          const list = Array.isArray(data) ? data : [];
          list.sort((a, b) => getClosedAt(b) - getClosedAt(a));
          setClosedConversations(list);
        }
      } catch {
        // Ignore  - badge will show 0
      }
    };

    fetchConversations();
    fetchClosedCount();

    return () => {
      active = false;
    };
  }, [currentSubscriptionId, refreshKey]);

  // Fetch closed conversations when switching to "closed" view
  useEffect(() => {
    if (viewMode !== 'closed') return;
    let active = true;
    setLoadingClosed(true);

    const fetchClosed = async () => {
      try {
        const [data] = await omnichannelService.getConversations({
          size: 100,
          filter: { status: ['resolved'] },
        });
        if (active) {
          const list = Array.isArray(data) ? data : [];
          list.sort((a, b) => getClosedAt(b) - getClosedAt(a));
          setClosedConversations(list);
        }
      } catch {
        if (active) setClosedConversations([]);
      } finally {
        if (active) setLoadingClosed(false);
      }
    };

    fetchClosed();
    return () => { active = false; };
  }, [viewMode]);

  // Auto-switch viewMode when the URL points to a closed conversation on initial page load.
  // Runs only once  - the flag persists across remounts (module-level).
  useEffect(() => {
    if (loading || _autoSwitchDone) return;
    _autoSwitchDone = true;
    const urlId = location.pathname.split('/').pop();
    // Only process valid ObjectId-like strings (24 hex chars)
    if (!urlId || !/^[a-f0-9]{24}$/.test(urlId)) return;

    const inActive = conversations.some(c => c._id === urlId);
    if (!inActive && closedConversations.some(c => c._id === urlId)) {
      setViewMode('closed');
    }
  }, [loading]);

  // SLA tick  - 1-second interval to keep countdowns live (only when SLA is configured)
  useEffect(() => {
    if (!slaConfig) return;
    const id = setInterval(() => setSlaTick(t => t + 1), 1_000);
    return () => clearInterval(id);
  }, [slaConfig]);

  // Register the live updateConversations callback so SSE handlers always call the current instance.
  _onSseEvent = updateConversations;

  // Navigate away when the currently open conversation is claimed by another agent.
  // Fires whenever conversations state changes (e.g., after omnichannel:conversation-assigned SSE).
  // Condition: selected conversation exists in the list AND has assignedTo set to someone else.
  useEffect(() => {
    const currentId = location.pathname.split('/').pop();
    if (!currentId || !/^[a-f0-9]{24}$/.test(currentId)) return;

    const conv = conversations.find(c => c._id === currentId);
    if (!conv) return;

    if (conv.assignedTo && String(conv.assignedTo) !== String(storedUser?._id)) {
      navigate(basePath, { replace: true });
    }
  }, [conversations]);

  // Listen for local archive/unarchive events dispatched by Conversation.jsx
  useEffect(() => {
    const handleArchiveLocal = e => {
      const { conversationId, archived } = e.detail || {};
      if (!conversationId) return;
      updateConversations(prev =>
        prev.map(c => (c._id === conversationId ? { ...c, archived } : c))
      );
    };
    window.addEventListener('omnichannel:archive-local', handleArchiveLocal);
    return () => window.removeEventListener('omnichannel:archive-local', handleArchiveLocal);
  }, []);

  // SSE effect  - connect the shared manager and subscribe to events relevant to the chat list.
  // One EventSource shared with Conversation.jsx  - no duplicate connections.
  useEffect(() => {
    const subscriptionId = storedSubscription?._id;
    if (!subscriptionId) return;

    sseConnect(subscriptionId);

    const unsubs = [
      sseSubscribe(
        'omnichannel:message-received',
        ({ conversationId, message, contactSnapshot, sourceType, isGroup }) => {
          if (!conversationId) return;
          _onSseEvent?.(prev => {
            const idx = prev.findIndex(c => c._id === conversationId);
            if (idx >= 0) {
              const existingMsgs = prev[idx].messages || [];
              // Dedup: backend emits twice (webhook handler + MongoDB change stream).
              // Skip message update and unread increment if already present.
              const isDup = existingMsgs.some(m =>
                (message?._id && m._id === message._id) ||
                (message?.externalMessageId && m.externalMessageId === message.externalMessageId)
              );
              if (isDup) return prev;
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                // Propagate sourceType/isGroup from SSE if the loaded conversation is missing them
                // (can happen for legacy documents that pre-date these fields)
                ...(sourceType && !updated[idx].sourceType ? { sourceType } : {}),
                ...(isGroup && !updated[idx].isGroup ? { isGroup } : {}),
                messages: [...existingMsgs, message],
                lastMessageAt: message?.timestamp,
                unreadCount: (updated[idx].unreadCount || 0) + 1,
              };
              updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
              return updated;
            }
            return [
              {
                _id: conversationId,
                channel: 'whatsapp',
                sourceType,
                isGroup: isGroup || false,
                contactSnapshot,
                messages: [message],
                lastMessageAt: message?.timestamp,
                unreadCount: 1,
                status: 'open',
              },
              ...prev,
            ];
          });
        }
      ),
      sseSubscribe('omnichannel:message-sent', ({ conversationId, message }) => {
        if (!conversationId || !message) return;
        _onSseEvent?.(prev => {
          const idx = prev.findIndex(c => c._id === conversationId);
          if (idx < 0) return prev;
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            messages: [...(updated[idx].messages || []), message],
            lastMessageAt: message.timestamp,
          };
          return updated;
        });
      }),
      sseSubscribe('omnichannel:conversation-read', ({ conversationId }) => {
        if (!conversationId) return;
        _onSseEvent?.(prev =>
          prev.map(c => (c._id === conversationId ? { ...c, unreadCount: 0 } : c))
        );
      }),
      sseSubscribe(
        'omnichannel:contact-snapshot-updated',
        ({ conversationId, contactSnapshot }) => {
          if (!conversationId || !contactSnapshot) return;
          _onSseEvent?.(prev =>
            prev.map(c =>
              c._id === conversationId
                ? { ...c, contactSnapshot: { ...c.contactSnapshot, ...contactSnapshot } }
                : c
            )
          );
        }
      ),
      sseSubscribe(
        'omnichannel:conversation-assigned',
        ({ conversationId, assignedTo: newAssignedTo }) => {
          if (!conversationId) return;
          _onSseEvent?.(prev =>
            prev.map(c => (c._id === conversationId ? { ...c, assignedTo: newAssignedTo } : c))
          );
        }
      ),
      sseSubscribe('omnichannel:context-closed', ({ conversationId, status }) => {
        if (!conversationId) return;
        if (status === 'resolved') {
          // Move conversation from active to closed list
          _onSseEvent?.(prev => {
            const closed = prev.find(c => c._id === conversationId);
            if (closed) {
              setClosedConversations(prevClosed => {
                // Avoid duplicates if the conversation already exists in the closed list
                const filtered = prevClosed.filter(c => c._id !== conversationId);
                return [{ ...closed, status: 'resolved' }, ...filtered];
              });
            }
            return prev.filter(c => c._id !== conversationId);
          });
        }
      }),
      sseSubscribe('omnichannel:conversation-archived', ({ externalId, archived }) => {
        if (!externalId) return;
        _onSseEvent?.(prev =>
          prev.map(c => (c.externalId === externalId ? { ...c, archived } : c))
        );
      }),
    ];

    return () => unsubs.forEach(fn => fn());
  }, [storedSubscription?._id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenChat = id => {
    navigate(`${basePath}/${id}`);
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchValue(value);

    // Deselect current conversation if it no longer matches the new search term
    if (value && currentId && /^[a-f0-9]{24}$/.test(currentId)) {
      const list = viewMode === 'active' ? conversations : closedConversations;
      const conv = list.find(c => c._id === currentId);
      if (conv) {
        const name = conv.contactSnapshot?.name || conv.displayName || '';
        const phone = conv.contactSnapshot?.phone || '';
        const email = conv.contactSnapshot?.email || '';
        if (!matchesSearch(name, phone, email, value)) {
          navigate(basePath);
        }
      }
    }
  };

  const handleViewModeChange = mode => {
    setViewMode(mode);
    // Deselect current conversation  - it belongs to the opposite status bucket
    if (currentId && /^[a-f0-9]{24}$/.test(currentId)) {
      navigate(basePath);
    }
  };

  const handleToggleGroups = () => {
    setShowGroups(v => {
      saveFilterPref(storedUser?._id, 'showGroups', !v);
      return !v;
    });
  };

  const handleToggleArchived = () => {
    const newValue = !showArchived;
    saveFilterPref(storedUser?._id, 'showArchived', newValue);
    setShowArchived(newValue);

    // If hiding archived and the currently viewed conversation is archived, navigate away
    if (!newValue) {
      const currentId = location.pathname.split('/').pop();
      const currentConv = conversations.find(c => c._id === currentId);
      if (currentConv?.archived) {
        queueMicrotask(() => navigate(basePath, { replace: true }));
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const currentId = location.pathname.split('/').pop();
  const myId = storedUser?._id;

  // Count groups and archived from ALL conversations (before filtering)
  const groupCount = conversations.filter(c => c.isGroup).length;
  const archivedCount = conversations.filter(c => c.archived).length;

  // Filter conversations by toggle filters and search value
  const filteredList = conversations.filter(item => {
    // Toggle filters: hide groups and archived unless toggled on
    if (!showGroups && item.isGroup) return false;
    if (!showArchived && item.archived) return false;

    // Text search filter (partial, regex-safe)
    const name = item.contactSnapshot?.name || item.displayName || '';
    const phone = item.contactSnapshot?.phone || '';
    const email = item.contactSnapshot?.email || '';
    return matchesSearch(name, phone, email, searchValue);
  });

  // Determine user role for visibility permissions
  const userRole = storedSubscription?.profile || 'guest';
  const isOwnerOrAdm = userRole === 'owner' || userRole === 'adm';

  // Separate into groups based on role
  const myConversations = filteredList.filter(
    item => item.assignedTo && String(item.assignedTo) === myId
  );
  const queueConversations = filteredList.filter(item => !item.assignedTo);

  // Owner/adm can see all conversations assigned to anyone (excluding those in queue)
  const othersConversations = isOwnerOrAdm
    ? filteredList.filter(item => item.assignedTo && String(item.assignedTo) !== myId)
    : [];

  // Closed conversations  - only show those assigned to the logged-in user, with search applied
  const myClosedConversations = closedConversations.filter(item => {
    if (!item.assignedTo || String(item.assignedTo) !== myId) return false;
    if (!searchValue) return true;
    const name = item.contactSnapshot?.name || item.displayName || '';
    const phone = item.contactSnapshot?.phone || '';
    const email = item.contactSnapshot?.email || '';
    return matchesSearch(name, phone, email, searchValue);
  });

  // Auto-expand the collapse group that contains the selected conversation.
  // No _id in URL → all collapsed. With _id → open the group where it belongs.
  const activeKeys = getActiveKeys(currentId, myConversations, queueConversations, othersConversations, isOwnerOrAdm);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="chat-menu" style={{ background: token.colorBgContainer, overflowX: 'hidden' }}>
      <ChatMenuHeader
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        showGroups={showGroups}
        onToggleGroups={handleToggleGroups}
        groupCount={groupCount}
        showArchived={showArchived}
        onToggleArchived={handleToggleArchived}
        archivedCount={archivedCount}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        activeCount={conversations.length}
        closedCount={myClosedConversations.length}
        token={token}
      />
      <ChatMenuBody
        loading={viewMode === 'active' ? loading : loadingClosed}
        myConversations={viewMode === 'active' ? myConversations : myClosedConversations}
        queueConversations={viewMode === 'active' ? queueConversations : []}
        othersConversations={viewMode === 'active' ? othersConversations : []}
        isOwnerOrAdm={isOwnerOrAdm}
        activeKeys={activeKeys}
        openKeys={openKeys}
        onOpenKeysChange={setOpenKeys}
        currentId={currentId}
        hoveredId={hoveredId}
        onHover={setHoveredId}
        onOpenChat={handleOpenChat}
        slaConfig={slaConfig}
        token={token}
        viewMode={viewMode}
      />
    </div>
  );

  // Syncs React state and module-level cache atomically.
  // Using the functional update pattern avoids stale closure issues in SSE listeners.
  function updateConversations(updater) {
    setConversations(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      _conversationsCache = next;
      // Tag the cache with the current subscriptionId so it can be invalidated on tenant switch
      _cacheSubscriptionId = currentSubscriptionId;
      // Defer parent notification to avoid setState-during-render warning
      queueMicrotask(() => onConversationsLoaded?.(next.length));
      return next;
    });
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ChatMenu;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Returns the Collapse activeKeys based on which group contains the selected conversation.
 * No currentId → all collapsed. With currentId → open the group where it belongs.
 */
// Get the closedAt date from the last closed context for sorting
function getClosedAt(conv) {
  const contexts = conv.contexts || [];
  for (let i = contexts.length - 1; i >= 0; i--) {
    if (contexts[i].closedAt) return new Date(contexts[i].closedAt);
  }
  return new Date(conv.lastMessageAt || 0);
}

function getActiveKeys(currentId, myConversations, queueConversations, othersConversations, isOwnerOrAdm) {
  if (!currentId) return [];

  if (myConversations.some(c => c._id === currentId)) return ['mine'];
  if (queueConversations.some(c => c._id === currentId)) return ['queue'];
  if (isOwnerOrAdm && othersConversations.some(c => c._id === currentId)) return ['others'];

  // Conversation not found in any group (maybe filtered out by search) → all collapsed
  return [];
}

// Persist filter preferences per user in localStorage
const FILTER_PREFS_KEY = 'omnichannel_chat_filters';

function loadFilterPref(userId, key) {
  if (!userId) return false;
  try {
    const raw = localStorage.getItem(FILTER_PREFS_KEY);
    if (!raw) return false;
    const prefs = JSON.parse(raw);
    return !!prefs[userId]?.[key];
  } catch {
    return false;
  }
}

function saveFilterPref(userId, key, value) {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(FILTER_PREFS_KEY);
    const prefs = raw ? JSON.parse(raw) : {};
    if (!prefs[userId]) prefs[userId] = {};
    prefs[userId][key] = value;
    localStorage.setItem(FILTER_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Returns true if any of name/phone/email matches the search term.
 * Supports partial matches via regex (special chars are escaped for safety).
 */
function matchesSearch(name, phone, email, term) {
  if (!term) return true;
  try {
    const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
    return regex.test(name) || regex.test(phone) || regex.test(email);
  } catch {
    return false;
  }
}

// HMR cleanup is handled by omnichannelSseManager.js
